import { isAxiosError, type AxiosError } from 'axios';
import {
  AppError,
  ApiError,
  AuthenticationError,
  AuthorizationError,
  NetworkError,
  ValidationError,
  TimeoutError,
  RateLimitError,
  ParseError,
  EErrorCategory,
} from './error-classes';

/**
 * Checks if the error is an instance of AppError (or any of its subclasses).
 *
 * @param error - The value to check
 * @returns True if the error is an AppError instance
 */
export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError;
}

/**
 * Checks if the error is an API error (AppError or Axios with HTTP status).
 *
 * @param error - The value to check
 * @returns True if the error is an API-related error
 */
export function isApiError(error: unknown): error is ApiError | AxiosError {
  if (error instanceof ApiError) return true;
  if (isAxiosError(error) && error.response?.status !== undefined) return true;
  return false;
}

/**
 * Checks if the error is a network connectivity issue.
 * Covers Axios network errors, fetch TypeError, and typed NetworkError.
 *
 * @param error - The value to check
 * @returns True if the error is network-related
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof NetworkError) return true;
  if (isAxiosError(error) && error.code === 'ERR_NETWORK') return true;
  if (
    error instanceof TypeError &&
    error.message.toLowerCase().includes('failed to fetch')
  )
    return true;
  return false;
}

/**
 * Checks if the error is a timeout.
 *
 * @param error - The value to check
 * @returns True if the error is a timeout
 */
export function isTimeoutError(error: unknown): boolean {
  if (error instanceof TimeoutError) return true;
  if (isAxiosError(error) && error.code === 'ECONNABORTED') return true;
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  return false;
}

/**
 * Checks if the error is an authentication error (401).
 *
 * @param error - The value to check
 * @returns True if the error is an authentication failure
 */
export function isAuthenticationError(error: unknown): boolean {
  if (error instanceof AuthenticationError) return true;
  if (isAxiosError(error) && error.response?.status === 401) return true;
  return false;
}

/**
 * Checks if the error is an authorization/access denied error (403).
 * This is consistent with the existing `checkIsAccessDeniedError` utility.
 *
 * @param error - The value to check
 * @returns True if the error is an authorization failure
 */
export function isAuthorizationError(error: unknown): boolean {
  if (error instanceof AuthorizationError) return true;
  if (isAxiosError(error) && error.response?.status === 403) return true;
  if (
    error &&
    typeof error === 'object' &&
    'status' in error &&
    (error as { status: number }).status === 403
  )
    return true;
  return false;
}

/**
 * Checks if the error is a validation error.
 *
 * @param error - The value to check
 * @returns True if the error is a validation failure
 */
export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError;
}

/**
 * Checks if the error is a rate limit (429) error.
 *
 * @param error - The value to check
 * @returns True if the error indicates rate limiting
 */
export function isRateLimitError(error: unknown): boolean {
  if (error instanceof RateLimitError) return true;
  if (isAxiosError(error) && error.response?.status === 429) return true;
  return false;
}

/**
 * Checks if the error is a "not found" (404) error.
 *
 * @param error - The value to check
 * @returns True if the error indicates a missing resource
 */
export function isNotFoundError(error: unknown): boolean {
  if (error instanceof AppError && error.category === EErrorCategory.NOT_FOUND)
    return true;
  if (isAxiosError(error) && error.response?.status === 404) return true;
  return false;
}

/**
 * Checks if the error is a conflict (409) error.
 * Consistent with the existing `checkIsConflictError` utility.
 *
 * @param error - The value to check
 * @returns True if the error indicates a resource conflict
 */
export function isConflictError(error: unknown): boolean {
  if (error instanceof AppError && error.category === EErrorCategory.CONFLICT)
    return true;
  if (isAxiosError(error) && error.response?.status === 409) return true;
  return false;
}

/**
 * Checks if the error is a parse/deserialization error.
 *
 * @param error - The value to check
 * @returns True if the error is a data parsing failure
 */
export function isParseError(error: unknown): error is ParseError {
  return error instanceof ParseError;
}

/**
 * Checks if the error is a server error (5xx).
 *
 * @param error - The value to check
 * @returns True if the error is a server-side failure
 */
export function isServerError(error: unknown): boolean {
  if (error instanceof ApiError && error.statusCode >= 500) return true;
  if (
    isAxiosError(error) &&
    error.response?.status !== undefined &&
    error.response.status >= 500
  )
    return true;
  return false;
}

/**
 * Extracts the HTTP status code from an error, if present.
 *
 * @param error - The error to extract status from
 * @returns The HTTP status code, or undefined if not available
 */
export function getErrorStatusCode(error: unknown): number | undefined {
  if (error instanceof ApiError) return error.statusCode;
  if (isAxiosError(error)) return error.response?.status;
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: unknown }).status;
    return typeof status === 'number' ? status : undefined;
  }
  return undefined;
}
